import { ChangeEvent, startTransition, useEffect, useState } from "react";
import {
  FieldValues,
  UseControllerProps,
  useController,
  useWatch
} from "react-hook-form";

import Button from "@mui/material/Button";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import type { SxProps } from "@mui/material/styles";
import useTheme from "@mui/material/styles/useTheme";

import { isValid } from "date-fns";

import { neutral } from "app/themes/colors";

import BasePicker from "./base-picker.component";
import TimeInput from "./time-input.component";
import {
  AM_PM,
  HOURS,
  MINUTES,
  dateToParts,
  formatDigit,
  timeStringToDate
} from "./time-picker.utils";

type TimePickerProps = {
  sx?: SxProps;
  onChange?: (value: Date) => void;
  onClose?: () => void;
  openPicker: boolean;
  showPicker?: () => void;
  hidePicker?: (_event?: MouseEvent | TouchEvent) => void;
  hideAllPickers?: () => void;
  disabled?: boolean;
  label?: string;
};

export type RhfTimePickerFieldProps<T extends FieldValues> = TimePickerProps &
  UseControllerProps<T>;

export default function TimePicker<T extends FieldValues>({
  control,
  name,
  onClose,
  openPicker,
  showPicker,
  hidePicker,
  disabled = false,
  label,
  sx
}: RhfTimePickerFieldProps<T>) {
  const { palette } = useTheme();

  const value = useWatch({ control, name: name as any });
  const {
    field: { onBlur, onChange, ...field },
    fieldState
  } = useController<T>({
    name: name as any,
    control,
    rules: {
      required: "Time is required"
    }
  });

  const [hour, setHour] = useState("");
  const [minute, setMinute] = useState("");
  const [amPm, setAmPm] = useState("");

  useEffect(() => {
    if (value && isValid(value)) {
      const parsed = dateToParts(value, "", "", "");
      setHour(parsed.hour);
      setMinute(parsed.minute);
      setAmPm(parsed.amPm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.getTime?.()]);

  useEffect(() => {
    if (
      hour !== "" &&
      minute !== "" &&
      amPm !== "" &&
      timeStringToDate(value, hour, minute, amPm) !== value
    ) {
      onChange(timeStringToDate(value, hour, minute, amPm));
      onBlur();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hour, minute, amPm, onChange]);

  const handleHours = (value: string) => {
    setHour(value);
  };

  const handleMinutes = (value: string) => {
    setMinute(value);
  };

  const handleAmPm = (value: string) => {
    setAmPm(value);
  };

  const handleClose = () => {
    hidePicker?.();
    onClose?.();
  };

  const handleTextFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    const timeString = event.target.value.trim();
    // Regular expression to match time format (HH:mm a)
    const match = timeString.match(/^(\d{1,2}):(\d{2})\s?(am|pm)?$/i);

    if (match) {
      const [, newHour, newMinute, newAmPm] = match;
      // Update state only if the format is correct
      setHour(formatDigit(newHour));
      setMinute(formatDigit(newMinute));
      if (newAmPm) {
        setAmPm(newAmPm.toLowerCase());
      }

      // Update the picker and field value if the time is valid
      const newDate = timeStringToDate(
        field.value,
        newHour,
        newMinute,
        newAmPm || amPm
      );
      if (isValid(newDate)) {
        onChange(newDate);
      }
    } else {
      if (timeString === "") {
        setHour("");
        setMinute("");
        setAmPm("");
        onChange(null);
      }
    }
  };

  return (
    <Stack sx={sx}>
      <TextField
        {...field}
        value={`${hour}:${minute} ${amPm}`}
        placeholder="hh:mm a"
        InputProps={{
          inputComponent: TimeInput
        }}
        onBlur={onBlur}
        onFocus={() => {
          if (disabled) return;
          startTransition(() => {
            showPicker?.();
          });
        }}
        label={label}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          startTransition(() => {
            showPicker?.();
          });
        }}
        error={Boolean(fieldState.error)}
        helperText={fieldState.error?.message}
        onChange={handleTextFieldChange}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            handleClose();
            event.preventDefault();
          }
        }}
      />
      {openPicker && (
        // Is it working? Looks like it should be onClickAway={hidePicker} or () => hidePicker()
        <ClickAwayListener onClickAway={() => hidePicker}>
          <Paper
            sx={{
              flexDirection: "row",
              display: "flex",
              transform: `translate(0,${label ? 60 : 32}px)`,
              background: palette.timePicker.bgcolor,
              position: "absolute",
              height: 205,
              width: 200,
              border: `solid 1px ${neutral[500]}`,
              zIndex: 2
            }}
          >
            <Stack direction="row">
              <BasePicker
                value={hour}
                options={HOURS}
                onClick={handleHours}
                onDoubleClick={handleClose}
              />
              <BasePicker
                value={minute}
                options={MINUTES}
                onClick={handleMinutes}
                onDoubleClick={handleClose}
              />
              <BasePicker
                value={amPm}
                options={AM_PM}
                onClick={handleAmPm}
                onDoubleClick={handleClose}
                sx={{ overflowY: "hidden" }}
              />
            </Stack>
            <Button
              variant="contained"
              size="small"
              onClick={handleClose}
              sx={{
                position: "absolute",
                top: 207,
                right: 0,
                zIndex: 2,
                width: 203
              }}
            >
              OK
            </Button>
          </Paper>
        </ClickAwayListener>
      )}
    </Stack>
  );
}
